/**
 * AppThemeCssVariables bond setup
 *
 * Wires @molecule/app-theme-css-variables to @molecule/app-theme.
 * Exports themeProvider for use in MoleculeProvider.
 */

import type { Theme } from '@molecule/app-theme'
import { setProvider } from '@molecule/app-theme'
import {
  createCSSVariablesThemeProvider,
  darkTheme,
  lightTheme as baseLightTheme,
} from '@molecule/app-theme-css-variables'

const defaultLightTheme: Theme = {
  ...baseLightTheme,
  name: 'light',
  mode: 'light',
  colors: {
    background: '#eaeaea',
    backgroundSecondary: '#e0e0e0',
    backgroundTertiary: '#d6d6d6',
    surface: '#f4f4f4',
    surfaceSecondary: '#eaeaea',
    text: '#1a1a1a',
    textSecondary: '#505050',
    textTertiary: '#6a6a6a',
    textInverse: '#f4f4f4',
    primary: '#3060c0',
    primaryLight: '#4070e0',
    primaryDark: '#204090',
    secondary: '#555555',
    secondaryLight: '#707070',
    secondaryDark: '#404040',
    success: '#1e7a00',
    successLight: '#d4edda',
    warning: '#7a6200',
    warningLight: '#fff3cd',
    error: '#b81c00',
    errorLight: '#f8d7da',
    info: '#117a8b',
    infoLight: '#d1ecf1',
    inputBackground: '#f4f4f4',
    border: '#b8b8b8',
    borderSecondary: '#a0a0a0',
    borderFocus: '#3060c0',
    overlay: 'rgba(0, 0, 0, 0.35)',
    shadow: 'rgba(0, 0, 0, 0.15)',
  },
  borderRadius: {
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },
}

const defaultDarkTheme: Theme = {
  ...darkTheme,
  name: 'dark',
  mode: 'dark',
  colors: {
    background: '#0e0e0e',
    backgroundSecondary: '#121212',
    backgroundTertiary: '#0a0a0a',
    surface: '#151515',
    surfaceSecondary: '#333333',
    text: '#e0e0e0',
    textSecondary: '#aaaaaa',
    textTertiary: '#bebebe',
    textInverse: '#222222',
    primary: '#4070e0',
    primaryLight: '#6090f0',
    primaryDark: '#3060c0',
    secondary: '#808080',
    secondaryLight: '#a0a0a0',
    secondaryDark: '#606060',
    success: '#309000',
    successLight: '#1e4620',
    warning: '#988828',
    warningLight: '#4d3800',
    error: '#d02000',
    errorLight: '#4d1a1f',
    info: '#17a2b8',
    infoLight: '#0d3d47',
    inputBackground: '#191919',
    border: '#191919',
    borderSecondary: '#292929',
    borderFocus: '#4070e0',
    overlay: 'rgba(0, 0, 0, 0.5)',
    shadow: 'rgba(0, 0, 0, 0.4)',
  },
  borderRadius: {
    none: '0',
    sm: '1px',
    md: '3px',
    lg: '6px',
    xl: '8px',
    full: '9999px',
  },
}

export const themeProvider = createCSSVariablesThemeProvider({
  themes: [defaultLightTheme, defaultDarkTheme],
  defaultTheme: 'dark',
  persistKey: 'molecule-theme',
  storage: typeof localStorage !== 'undefined' ? localStorage : undefined,
})

export function setupAppThemeCssVariables(): void {
  setProvider(themeProvider)
}
